import type {
  IAnalyticsTag,
  IWorkerTimesheetEntry,
} from '@apis/workerTimesheet';

/**
 * Derived view types for the Worker Timesheet report page.
 *
 * These wrap the wire response from `fetchWorkerTimesheet` with the
 * presentation-layer shape the UI components consume:
 *   - per-tag KPIs computed from the entries list,
 *   - grouped indices keyed by kind (worker/manager) and tradeId,
 *   - an ordered `tagOrder` list mirroring the backend's request order.
 */

export interface IKpis {
  /** Total work hours divided by working days count, 2 decimals. */
  avgDailyHours: number;
  /** Unique `dateIso` values amongst entries with a non-null `exitIso`. */
  workingDaysCount: number;
  /** Sum of `(exit - arrival)` in hours, 2 decimals. Open shifts skipped. */
  totalWorkingHours: number;
}

export interface ITagTimesheetView {
  tagId: string;
  tag: IAnalyticsTag;
  entries: IWorkerTimesheetEntry[];
  absenceDates: string[];
  kpis: IKpis;
}

/**
 * Bucket of tag views indexed by tradeId. Tags whose profession is null
 * land under the synthetic `__no-trade__` key so the iteration order in
 * the UI is stable and predictable.
 */
export type ITradeBucket = Record<string, ITagTimesheetView[]>;

export interface IGroupedView {
  byTag: Record<string, ITagTimesheetView>;
  groupedByKind: Record<'worker' | 'manager', ITradeBucket>;
  /** Tag order as returned by the backend (request order). */
  tagOrder: string[];
}

/** Marker tradeId used for tags whose `profession` is null. */
export const NO_TRADE_ID = '__no-trade__';
